import React, { FC, useEffect, useMemo } from 'react';
import {
  Grid,
  useT,
  Button,
  FInput,
  FButton,
  FFormik,
  validateSchema,
  validateObject,
  Input,
  ErrorMessage,
} from '@frontegg/react-core';
import { useConnectivityActions, useConnectivityState } from '@frontegg/react-hooks';

export interface IConnectivityWebhookTestForm {
  secret: string;
  url: string;
  toggleTestDialog: () => void;
}

export const ConnectivityWebhooksTestForm: FC<IConnectivityWebhookTestForm> = ({ secret, url, toggleTestDialog }) => {
  const { t } = useT();
  const { isTesting, testResult } = useConnectivityState();
  const { cleanWebhookTestData, postWebhookTestAction } = useConnectivityActions();

  useEffect(() => {
    return () => {
      cleanWebhookTestData();
    };
  }, [cleanWebhookTestData]);

  const validationSchema = validateSchema({
    payload: validateObject(t('common.payload'), t),
  });

  const btnVariant = useMemo(() => {
    switch (testResult?.status) {
      case 'failed':
        return 'danger';
      case 'success':
        return 'success';
      default:
        return undefined;
    }
  }, [testResult?.status]);

  return (
    <FFormik.Formik
      validationSchema={validationSchema}
      initialValues={{ payload: '' }}
      onSubmit={({ payload }, { setSubmitting }) => {
        postWebhookTestAction({
          url,
          secret: secret ? secret : null,
          payload: { ...JSON.parse(payload) },
        });
        setSubmitting(false);
      }}
    >
      <FFormik.Form>
        <Grid container wrap='nowrap'>
          <Grid container className='fe-connectivity-webhook-test-settings' direction='column' xs={12}>
            <Input data-test-id='urlBox' className='fe-mb-1' label='URL' placeholder='https://' value={url} disabled />
            <Input
              data-test-id='secretBox'
              className='fe-mb-1'
              label={t('common.secretKey')}
              placeholder={t('common.secretKey')}
              value={secret}
              disabled
            />
            <FInput
              data-test-id='testBox'
              className='fe-connectivity-webhook-test-payload'
              label={t('connectivity.json')}
              name='payload'
              multiline
              placeholder={t('connectivity.json')}
            />
          </Grid>
        </Grid>

        {testResult?.status === 'failed' && <ErrorMessage error={testResult.message} />}
        {testResult?.status === 'success' && <div className='fe-success-message fe-center'>{testResult.message}</div>}

        <div className='fe-dialog__footer'>
          <Grid container justifyContent='space-between'>
            <Grid>
              <Button data-test-id='closeBtn' onClick={toggleTestDialog} size='large'>
                {t('common.close')}
              </Button>
            </Grid>
            <Grid>
              <FButton size='large' data-test-id='submitBtn' variant={btnVariant} loading={isTesting} type='submit'>
                {testResult?.status?.toUpperCase() ?? t('connectivity.testHook').toUpperCase()}
              </FButton>
            </Grid>
          </Grid>
        </div>
      </FFormik.Form>
    </FFormik.Formik>
  );
};
